import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { AppSettings, TracingSettings, TracingBackend, TracingProviderConfig, WebTracingSettings, WebTracingBackend } from '../../types/settings';
import { LogLevel, ComponentCode, ComponentName } from '@padcanvas/shared';
import { LOG_LEVEL_OPTIONS } from '@shared-ui/logging';
import './Logging.css';

const ALL_COMPONENT_CODES = Object.values(ComponentCode).filter((v): v is ComponentCode => typeof v === 'number');
const COMPONENTS = ALL_COMPONENT_CODES
  .filter(code => code < 60)
  .map(code => ({ code, name: ComponentName[code] }));

const WEB_COMPONENTS = ALL_COMPONENT_CODES
  .filter(code => code >= 60)
  .map(code => ({ code, name: ComponentName[code] }));

const TRACING_BACKENDS: { value: TracingBackend; label: string; description: string }[] = [
  { value: 'console', label: 'Console', description: 'Log to Azure Functions console' },
  { value: 'cosmosdb', label: 'CosmosDB', description: 'Persist logs to CosmosDB container' },
];

const WEB_TRACING_BACKENDS: { value: WebTracingBackend; label: string; description: string }[] = [
  { value: 'web-console', label: 'Browser Console', description: 'Log to browser developer console' },
  { value: 'web-server', label: 'Server', description: 'Send logs to backend (batched)' },
  { value: 'web-popup', label: 'Toast/Popup', description: 'Show error/warning toasts to user' },
];

const DEFAULT_TRACING: TracingSettings = {
  providers: {
    console: { enabled: true, logLevel: LogLevel.DEBUG },
    cosmosdb: { enabled: false, logLevel: LogLevel.WARN }
  },
  componentLogLevels: {},
  includeStackInResponse: false,
  includeTechnicalDetails: false,
  debugRequestIds: []
};

const DEFAULT_WEB_TRACING: WebTracingSettings = {
  providers: {
    'web-console': { enabled: true, logLevel: LogLevel.DEBUG },
    'web-server': { enabled: false, logLevel: LogLevel.WARN },
    'web-popup': { enabled: true, logLevel: LogLevel.WARN },
  },
  componentLogLevels: {},
};

function migrateTracingSettings(tracing: TracingSettings | undefined): TracingSettings {
  if (!tracing) return DEFAULT_TRACING;
  if (tracing.providers) return tracing;

  const providers: Partial<Record<TracingBackend, TracingProviderConfig>> = {};
  providers.console = {
    enabled: true,
    logLevel: tracing.providerLogLevels?.console ?? LogLevel.DEBUG
  };

  if (tracing.backend && tracing.backend !== 'console') {
    providers[tracing.backend] = {
      enabled: true,
      logLevel: tracing.providerLogLevels?.[tracing.backend] ?? tracing.defaultLogLevel ?? LogLevel.WARN
    };
  }

  if (!providers.cosmosdb && tracing.providerLogLevels?.cosmosdb) {
    providers.cosmosdb = {
      enabled: false,
      logLevel: tracing.providerLogLevels.cosmosdb
    };
  }

  return {
    providers,
    componentLogLevels: tracing.componentLogLevels || {},
    includeStackInResponse: tracing.includeStackInResponse || false,
    includeTechnicalDetails: tracing.includeTechnicalDetails || false,
    debugRequestIds: tracing.debugRequestIds || []
  };
}

export function Logging() {
  const queryClient = useQueryClient();
  const [tracingSettings, setTracingSettings] = useState<TracingSettings>(DEFAULT_TRACING);
  const [webTracingSettings, setWebTracingSettings] = useState<WebTracingSettings>(DEFAULT_WEB_TRACING);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  useEffect(() => {
    if (settings) {
      setTracingSettings(migrateTracingSettings(settings.tracing));
      setWebTracingSettings(settings.webTracing || DEFAULT_WEB_TRACING);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (newSettings: Partial<AppSettings>) => adminApi.updateSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const handleTracingProviderToggle = (backend: TracingBackend) => {
    const currentConfig = tracingSettings.providers?.[backend] || { enabled: false, logLevel: LogLevel.INFO1 };
    const newProviders = {
      ...tracingSettings.providers,
      [backend]: { ...currentConfig, enabled: !currentConfig.enabled }
    };
    const newTracing = { ...tracingSettings, providers: newProviders };
    setTracingSettings(newTracing);
    mutation.mutate({ tracing: newTracing });
  };

  const handleTracingLogLevelChange = (backend: TracingBackend, level: LogLevel) => {
    const currentConfig = tracingSettings.providers?.[backend] || { enabled: false, logLevel: LogLevel.INFO1 };
    const newProviders = {
      ...tracingSettings.providers,
      [backend]: { ...currentConfig, logLevel: level }
    };
    const newTracing = { ...tracingSettings, providers: newProviders };
    setTracingSettings(newTracing);
    mutation.mutate({ tracing: newTracing });
  };

  const handleTracingToggle = (field: 'includeStackInResponse' | 'includeTechnicalDetails') => {
    const newTracing = { ...tracingSettings, [field]: !tracingSettings[field] };
    setTracingSettings(newTracing);
    mutation.mutate({ tracing: newTracing });
  };

  const handleComponentLogLevelChange = (component: ComponentCode, level: LogLevel | null) => {
    const newComponentLevels = { ...tracingSettings.componentLogLevels };
    if (level === null) {
      delete newComponentLevels[component];
    } else {
      newComponentLevels[component] = level;
    }
    const newTracing = { ...tracingSettings, componentLogLevels: newComponentLevels };
    setTracingSettings(newTracing);
    mutation.mutate({ tracing: newTracing });
  };

  const handleWebTracingProviderToggle = (backend: WebTracingBackend) => {
    const currentConfig = webTracingSettings.providers?.[backend] || { enabled: false, logLevel: LogLevel.INFO1 };
    const newProviders = {
      ...webTracingSettings.providers,
      [backend]: { ...currentConfig, enabled: !currentConfig.enabled }
    };
    const newWebTracing = { ...webTracingSettings, providers: newProviders };
    setWebTracingSettings(newWebTracing);
    mutation.mutate({ webTracing: newWebTracing });
  };

  const handleWebTracingLogLevelChange = (backend: WebTracingBackend, level: LogLevel) => {
    const currentConfig = webTracingSettings.providers?.[backend] || { enabled: false, logLevel: LogLevel.INFO1 };
    const newProviders = {
      ...webTracingSettings.providers,
      [backend]: { ...currentConfig, logLevel: level }
    };
    const newWebTracing = { ...webTracingSettings, providers: newProviders };
    setWebTracingSettings(newWebTracing);
    mutation.mutate({ webTracing: newWebTracing });
  };

  const handleWebComponentLogLevelChange = (component: ComponentCode, level: LogLevel | null) => {
    const newComponentLevels = { ...webTracingSettings.componentLogLevels };
    if (level === null) {
      delete newComponentLevels[component];
    } else {
      newComponentLevels[component] = level;
    }
    const newWebTracing = { ...webTracingSettings, componentLogLevels: newComponentLevels };
    setWebTracingSettings(newWebTracing);
    mutation.mutate({ webTracing: newWebTracing });
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Logging Providers</h2>
        <p className="section-description">
          Enable multiple logging backends with independent log levels.
        </p>
      </div>

      <div className="logging-providers">
        {TRACING_BACKENDS.map((backend) => {
          const config = tracingSettings.providers?.[backend.value] || { enabled: false, logLevel: LogLevel.INFO1 };

          return (
            <div key={backend.value} className={`logging-provider-card ${config.enabled ? 'enabled' : ''}`}>
              <div className="provider-header">
                <label className="provider-toggle">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={() => handleTracingProviderToggle(backend.value)}
                    disabled={mutation.isPending}
                  />
                  <span className="provider-name">{backend.label}</span>
                </label>
                <span className="provider-description">{backend.description}</span>
              </div>
              <div className="provider-settings">
                <label className="log-level-selector">
                  <span>Log Level:</span>
                  <select
                    value={config.logLevel}
                    onChange={(e) => handleTracingLogLevelChange(backend.value, Number(e.target.value) as LogLevel)}
                    disabled={mutation.isPending || !config.enabled}
                  >
                    {LOG_LEVEL_OPTIONS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Component Log Levels */}
      <div className="component-levels-section">
        <h3>Component Log Levels</h3>
        <p className="section-description">
          Override log level for specific components. Leave as "Default" to use the provider's level.
        </p>
        <div className="component-levels-grid">
          {COMPONENTS.map((component) => {
            const currentLevel = tracingSettings.componentLogLevels?.[component.code];
            return (
              <div key={component.code} className="component-level-row">
                <span className="component-name">{component.name}</span>
                <select
                  value={currentLevel ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleComponentLogLevelChange(
                      component.code,
                      val === '' ? null : Number(val) as LogLevel
                    );
                  }}
                  disabled={mutation.isPending}
                >
                  <option value="">Default</option>
                  {LOG_LEVEL_OPTIONS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Debug Options */}
      <div className="debug-options-section">
        <h3>Debug Options</h3>
        <div className="debug-options-grid">
          <label className={`debug-option ${tracingSettings.includeTechnicalDetails ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={tracingSettings.includeTechnicalDetails}
              onChange={() => handleTracingToggle('includeTechnicalDetails')}
              disabled={mutation.isPending}
            />
            <div className="debug-option-content">
              <span className="debug-option-label">Include Technical Details</span>
              <span className="debug-option-description">Add context info to error responses</span>
            </div>
          </label>
          <label className={`debug-option ${tracingSettings.includeStackInResponse ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={tracingSettings.includeStackInResponse}
              onChange={() => handleTracingToggle('includeStackInResponse')}
              disabled={mutation.isPending}
            />
            <div className="debug-option-content">
              <span className="debug-option-label">Include Stack Traces</span>
              <span className="debug-option-description">Add stack traces to error responses (dev only)</span>
            </div>
          </label>
        </div>
      </div>

      {/* Web App Logging */}
      <div className="section-header" style={{ marginTop: '32px' }}>
        <h2>Web App Logging</h2>
        <p className="section-description">
          Configure logging for student, teacher, and admin web apps. Changes take effect when apps reload.
        </p>
      </div>

      <div className="logging-providers">
        {WEB_TRACING_BACKENDS.map((backend) => {
          const config = webTracingSettings.providers?.[backend.value] || { enabled: false, logLevel: LogLevel.INFO1 };

          return (
            <div key={backend.value} className={`logging-provider-card ${config.enabled ? 'enabled' : ''}`}>
              <div className="provider-header">
                <label className="provider-toggle">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={() => handleWebTracingProviderToggle(backend.value)}
                    disabled={mutation.isPending}
                  />
                  <span className="provider-name">{backend.label}</span>
                </label>
                <span className="provider-description">{backend.description}</span>
              </div>
              <div className="provider-settings">
                <label className="log-level-selector">
                  <span>Log Level:</span>
                  <select
                    value={config.logLevel}
                    onChange={(e) => handleWebTracingLogLevelChange(backend.value, Number(e.target.value) as LogLevel)}
                    disabled={mutation.isPending || !config.enabled}
                  >
                    {LOG_LEVEL_OPTIONS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Web Component Log Levels */}
      <div className="component-levels-section">
        <h3>Web Component Log Levels</h3>
        <p className="section-description">
          Override log level for specific web components. Leave as "Default" to use the provider's level.
        </p>
        <div className="component-levels-grid">
          {WEB_COMPONENTS.map((component) => {
            const currentLevel = webTracingSettings.componentLogLevels?.[component.code];
            return (
              <div key={component.code} className="component-level-row">
                <span className="component-name">{component.name}</span>
                <select
                  value={currentLevel ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleWebComponentLogLevelChange(
                      component.code,
                      val === '' ? null : Number(val) as LogLevel
                    );
                  }}
                  disabled={mutation.isPending}
                >
                  <option value="">Default</option>
                  {LOG_LEVEL_OPTIONS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
